import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, ClipboardList, Store, Wrench, LogOut, Monitor, Minus, Plus, Send, Loader2, Search } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";
import BackButton from "../components/BackButton";
import PushToggle from "../components/mobile/PushToggle";
import MobileClients from "../components/mobile/MobileClients";
import MobileComodato from "../components/mobile/MobileComodato";
import { StatusBadge } from "../components/DashboardShell";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";
import api, { eur, formatApiErrorDetail } from "../lib/api";

const TABS = [
  { id: "ordine", label: "Ordine", icon: ShoppingCart },
  { id: "ordini", label: "I miei ordini", icon: ClipboardList },
  { id: "clienti", label: "Clienti", icon: Store },
  { id: "comodato", label: "Comodato", icon: Wrench },
];
const CATS = [["fusti", "Fusti"], ["granite", "Granite"], ["liquori", "Liquori"], ["collezione", "Collezione"]];

function QuickOrder({ user, onDone }) {
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientName, setClientName] = useState("");
  const [qty, setQty] = useState({});
  const [notes, setNotes] = useState("");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("fusti");
  const [busy, setBusy] = useState(false);
  const discount = Number(user?.discount_pct ?? 0);

  useEffect(() => {
    Promise.all([api.get("/products"), api.get("/clients")]).then(([p, c]) => { setProducts(p.data.filter((x) => x.active !== false)); setClients(c.data); }).catch(() => {});
  }, []);
  const price = (p) => Math.round((p.agent_price || 0) * (1 - discount / 100) * 100) / 100;
  const set = (id, n) => setQty((s) => ({ ...s, [id]: Math.max(0, Math.min(999, n)) }));
  const lines = useMemo(() => products.filter((p) => (qty[p.id] || 0) > 0), [products, qty]);
  const total = lines.reduce((s, p) => s + price(p) * qty[p.id], 0);
  const visible = products.filter((p) => p.category === cat && (!q || p.name.toLowerCase().includes(q.toLowerCase())));

  const submit = async () => {
    const c = clients.find((x) => x.name === clientName);
    if (!c || lines.length === 0) { toast.error("Scegli il cliente e almeno un prodotto"); return; }
    setBusy(true);
    try {
      await api.post("/orders", { client_name: c.name, client_type: c.type || "", client_city: c.city || "", notes, new_point: false,
        items: lines.map((p) => ({ product_id: p.id, name: p.name, quantity: qty[p.id], unit_price: price(p), category: p.category, portions: p.portions })) });
      toast.success("Ordine inviato"); setQty({}); setNotes(""); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="agent-quick-order">
      <h1 className="font-serif text-2xl font-semibold text-[#0B2A45] mb-3">Ordine rapido</h1>
      <Select value={clientName} onValueChange={setClientName}>
        <SelectTrigger className="h-12 mb-3" data-testid="agent-mobile-client-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
        <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}{c.city && ` — ${c.city}`}</SelectItem>)}</SelectContent>
      </Select>
      {clients.length === 0 && <p className="text-xs text-amber-700 mb-3">Nessun cliente: aggiungilo dalla tab Clienti.</p>}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 mb-2">
        {CATS.map(([v, l]) => <button key={v} onClick={() => setCat(v)} data-testid={`agent-mobile-cat-${v}`} className={`shrink-0 px-4 h-9 rounded-full text-sm font-semibold ${cat === v ? "bg-[#0F4C81] text-white" : "bg-white border border-stone-200 text-stone-600"}`}>{l}</button>)}
      </div>
      <div className="relative mb-3"><Search className="w-4 h-4 absolute left-3 top-3.5 text-stone-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca prodotto…" className="pl-9 h-11" data-testid="agent-mobile-search" /></div>
      <div className="bg-white rounded-2xl border border-stone-200 divide-y divide-stone-100">
        {visible.map((p) => (
          <div key={p.id} className="flex items-center gap-3 p-3" data-testid={`agent-mobile-row-${p.id}`}>
            <div className="flex-1 min-w-0"><div className="font-semibold text-[#0B2A45] truncate">{p.name}</div><div className="text-xs text-stone-500">{p.format} · <span className="font-mono">{eur(price(p))}</span></div></div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => set(p.id, (qty[p.id] || 0) - 1)} className="w-10 h-10 rounded-full border border-stone-200 flex items-center justify-center" data-testid={`agent-mobile-minus-${p.id}`}><Minus className="w-4 h-4" /></button>
              <span className="w-8 text-center font-mono text-sm" data-testid={`agent-mobile-qty-${p.id}`}>{qty[p.id] || 0}</span>
              <button onClick={() => set(p.id, (qty[p.id] || 0) + 1)} className="w-10 h-10 rounded-full bg-[#0F4C81] text-white flex items-center justify-center" data-testid={`agent-mobile-plus-${p.id}`}><Plus className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      {lines.length > 0 && (
        <div className="fixed left-0 right-0 bottom-16 z-30 px-4" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="agent-mobile-summary">
          <div className="bg-[#0B2A45] text-white rounded-2xl p-4 shadow-2xl">
            <div className="flex justify-between text-sm mb-2"><span>{lines.length} prodotti · {clientName || "cliente da scegliere"}</span><span className="font-mono font-bold" data-testid="agent-mobile-total">{eur(total)}</span></div>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note…" rows={1} className="bg-white/10 border-white/20 text-white placeholder:text-white/50 mb-2 min-h-[38px]" />
            <button onClick={submit} disabled={busy} className="w-full h-12 rounded-xl bg-[#FDE047] text-[#0B2A45] font-bold flex items-center justify-center gap-2" data-testid="agent-mobile-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" />Invia ordine</>}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [openId, setOpenId] = useState(null);
  useEffect(() => { api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {}); }, []);
  return (
    <div data-testid="agent-mobile-orders">
      <h1 className="font-serif text-2xl font-semibold text-[#0B2A45] mb-3">I miei ordini <span className="text-stone-400 text-base">({orders.length})</span></h1>
      <div className="space-y-3">
        {orders.length === 0 && <p className="text-center text-stone-400 py-10">Nessun ordine</p>}
        {orders.map((o) => (
          <button key={o.id} onClick={() => setOpenId(openId === o.id ? null : o.id)} className="w-full text-left bg-white rounded-2xl border border-stone-200 p-4" data-testid={`agent-mobile-order-${o.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0"><div className="font-semibold text-[#0B2A45] truncate">{o.client_name}</div><div className="text-xs text-stone-500">{new Date(o.created_at).toLocaleDateString("it-IT")}{o.source === "b2b" && " · dal listino cliente"}</div></div>
              <div className="text-right shrink-0"><div className="font-mono font-semibold text-[#0F4C81]">{eur(o.total)}</div><StatusBadge status={o.status} /></div>
            </div>
            {openId === o.id && <div className="mt-3 pt-3 border-t border-stone-100 text-sm space-y-1">{(o.items || []).map((it, i) => <div key={`${it.product_id || it.name}-${i}`} className="flex justify-between text-stone-600"><span>{it.quantity} × {it.name}</span><span className="font-mono">{eur(it.quantity * it.unit_price)}</span></div>)}<div className="flex justify-between text-xs text-stone-400 pt-1"><span>Provvigione</span><span className="font-mono text-[#15803D]">{eur(o.commission_amount)}</span></div></div>}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AgentMobile() {
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "ordine");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen paper-grain flex flex-col" data-testid="agent-mobile">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-stone-200" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2"><BackButton className="px-2" /><Logo size={32} /><div><div className="font-serif text-base font-bold text-[#0B2A45] leading-tight">GS Agenti</div><div className="text-[11px] text-stone-500 leading-tight" data-testid="agent-mobile-user">{user?.name}</div></div></div>
          <div className="flex items-center gap-1"><PushToggle /><a href="/agente" className="p-2 text-stone-500" data-testid="agent-mobile-open-desktop"><Monitor className="w-5 h-5" /></a><button onClick={async () => { await logout(); navigate("/"); }} className="p-2 text-stone-600" data-testid="agent-mobile-logout"><LogOut className="w-5 h-5" /></button></div>
        </div>
      </header>
      <main className="flex-1 px-4 py-4 pb-48">
        {tab === "ordine" && <QuickOrder user={user} onDone={() => setTab("ordini")} />}
        {tab === "ordini" && <MyOrders />}
        {tab === "clienti" && <MobileClients />}
        {tab === "comodato" && <MobileComodato />}
      </main>
      <nav className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-stone-200" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="agent-mobile-tabbar">
        <div className="grid grid-cols-4 h-16">
          {TABS.map(({ id, label, icon: Icon }) => <button key={id} onClick={() => setTab(id)} data-testid={`agent-mobile-tab-${id}`} className={`flex flex-col items-center justify-center gap-1 text-[11px] font-semibold ${tab === id ? "text-[#0F4C81]" : "text-stone-400"}`}><Icon className={`w-5 h-5 ${tab === id ? "stroke-[2.5]" : ""}`} />{label}</button>)}
        </div>
      </nav>
    </div>
  );
}
